// Plan 06-05 Task 3 — DashboardPage.
//
// 3 cards numéricos. NUNCA renderiza ciphertext nem detalhes de caso
// (D-150 ADM-04). Apenas counts agregados:
//   1. Total de casos no sistema (/api/admin/cases/count — SÓ a cardinalidade; o /cases é
//      403 por desenho, ADR-0002)
//   2. Total de membros (/api/admin/users)
//   3. Pedidos de cofre abertos (/api/cofre/requests, filtrados client-side)

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Card } from '@aions/ui-primitives';
import { apiFetch } from '../lib/api-client';
import { ComposicaoStatusBanner } from '../components/ComposicaoStatusBanner';

interface AdminUser {
  id: string;
  email: string;
  roles: string[];
  active: boolean;
}
interface CofreRequestSummary {
  requestId: string;
  stateKind: string;
}

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const [casesCount, setCasesCount] = useState<number | 'indisponivel' | null>(null);
  const [membersCount, setMembersCount] = useState<number | 'indisponivel' | null>(null);
  const [cofreOpenCount, setCofreOpenCount] = useState<number | 'indisponivel' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [casesResp, usersResp, cofreResp] = await Promise.all([
          // 🔴 `/cases/count`, não `/cases` (24/08). O `/cases` é 403 por desenho — ADR-0002,
          // o `admin_ti` não lista denúncias. O count devolve UM inteiro e nada por caso; é
          // o que este card sempre quis, e o que o cabeçalho desta tela já prometia
          // ("apenas counts agregados", D-150 ADM-04).
          apiFetch('/api/admin/cases/count'),
          apiFetch('/api/admin/users'),
          apiFetch('/api/cofre/requests'),
        ]);
        if (cancelled) return;

        // 🔴 `0` ERA MENTIRA (relato do operador, 23/08: "não está puxando a quantidade
        // correta de casos").
        //
        // `/api/admin/cases` devolve **403 `forbidden_admin_role`** por DESENHO — o próprio
        // detail diz: _"admin_ti não tem permissão para listar casos. Esta visibilidade
        // pertence exclusivamente aos membros do comitê"_ (ADR-0002). O `else` antigo
        // transformava essa recusa em `0`, e `0` é indistinguível de "não há caso nenhum".
        // É o "fallback plausível esconde ausência" do CLAUDE.md: o número plausível apaga
        // a diferença entre "medi e deu zero" e "não me deixaram medir".
        //
        // `null` continua sendo "carregando"; `'indisponivel'` é o estado novo, e a tela o
        // rotula em vez de inventar contagem.
        if (casesResp.ok) {
          const data = (await casesResp.json()) as { total?: number };
          // ⚠️ `typeof number`, não `?? 0`. Um corpo inesperado tem de cair em
          // "indisponível", não em zero — é o mesmo defeito que este bloco veio consertar.
          setCasesCount(typeof data.total === 'number' ? data.total : 'indisponivel');
        } else {
          setCasesCount('indisponivel');
        }

        if (usersResp.ok) {
          const data = (await usersResp.json()) as { users: AdminUser[] };
          setMembersCount(data.users.filter((u) => u.active).length);
        } else {
          // 🔴 ERA `0` (25/08). Mesmo defeito que o card de casos consertou logo acima, na
          // MESMA tela: `/api/admin/users` devolve 403 para quem não é `admin_ti`, e o `else`
          // transformava a recusa em "0 membros". Zero é plausível e indistinguível de "não
          // me deixaram medir" — e num painel de composição de comitê, "0 membros" sugere
          // comitê inexistente, que é pior que um rótulo honesto.
          setMembersCount('indisponivel');
        }

        if (cofreResp.ok) {
          const data = (await cofreResp.json()) as {
            requests?: CofreRequestSummary[];
          };
          const list = data.requests ?? [];
          setCofreOpenCount(
            list.filter(
              (r) => r.stateKind === 'Solicitado' || r.stateKind === 'AssinaturasReunidas',
            ).length,
          );
        } else {
          // 🔴 ERA `0` (25/08) — idem. "0 pedidos de cofre abertos" com a rota recusando é a
          // pior leitura possível: sugere que não há break-glass pendente quando pode haver.
          setCofreOpenCount('indisponivel');
        }
      } catch {
        if (!cancelled) setError(t('common.error_network'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <section>
      {/* Phase 2.2 D-210 — banner crítico de composição mínima
          (ADM-02 vermelho non-op + ADM-03 amarelo externo pendente).
          Mounted no TOPO antes de qualquer outro conteúdo. */}
      <ComposicaoStatusBanner />

      <h1>{t('dashboard.title')}</h1>

      {error && (
        <Alert variant="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      <div className="metric-grid">
        {(
          [
            { key: 'cases', label: t('dashboard.cases_total'), value: casesCount },
            { key: 'members', label: t('dashboard.members_total'), value: membersCount },
            { key: 'cofre', label: t('dashboard.cofre_open'), value: cofreOpenCount },
          ] as const
        ).map((m) => (
          <Card key={m.key} className="metric-card" aria-label={m.label}>
            <div className="metric-card__label">{m.label}</div>
            {m.value === 'indisponivel' ? (
              <div className="metric-card__value metric-card__value--na">
                {t('dashboard.indisponivel')}
              </div>
            ) : m.value === null ? (
              // Skeleton no lugar do '…' — anuncia carregando (role=status).
              <div className="metric-card__value" role="status" aria-label={t('common.loading')}>
                <span className="skeleton-line skeleton-line--metric" aria-hidden="true" />
              </div>
            ) : (
              <div className="metric-card__value">{String(m.value)}</div>
            )}
          </Card>
        ))}
      </div>
    </section>
  );
}
